import { createError } from '@common/neo.tax';

import {
  GetSredPayrollExpensesQuery,
  GetSredPayrollExpensesQueryVariables,
  GET_SRED_PAYROLL_EXPENSES,
  SredPayrollExpenseFilter,
  SredPayrollExpenseSort,
  useCursorPaginatedQuery,
  useSticky,
  Conditions,
  advancedFilter,
  FilterField,
  keywordFilter,
} from '@neo.tax/shared';

export type SredPayrollExpense = NonNullable<
  GetSredPayrollExpensesQuery['getSredPayrollExpenses']['edges']
>[number]['node'];

export type WithSredPayrollExpense<T extends SredPayrollExpense> = Omit<T, 'sredPayrollExpenseId'> & {
  sredPayrollExpenseId: string;
};

export const isSredPayrollExpenseWithPayrollExpense = (
  expense: SredPayrollExpense,
): expense is WithSredPayrollExpense<SredPayrollExpense> => expense.sredPayrollExpenseId != null;

const GET_SRED_PAYROLL_EXPENSES_KEY = 'GET_SRED_PAYROLL_EXPENSES_KEY';

interface UseSredPayrollExpensesProps {
  sredTaxCreditId: string;
  filterConditions?: Conditions<FilterField<SredPayrollExpenseFilter>>;
  searchKeyword?: string | null;
  sorting?: Array<SredPayrollExpenseSort> | SredPayrollExpenseSort;
}

export const useSredPayrollExpenses = ({
  filterConditions,
  searchKeyword,
  sorting,
  sredTaxCreditId,
}: UseSredPayrollExpensesProps): {
  loading: boolean;
  sredPayrollExpenses: SredPayrollExpense[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetSredPayrollExpensesQuery, GetSredPayrollExpensesQueryVariables>(
    GET_SRED_PAYROLL_EXPENSES,
    {
      variables: {
        sredTaxCreditId,
        filter: {
          and: [
            keywordFilter(['name', 'title'], searchKeyword),
            advancedFilter<SredPayrollExpenseFilter>(filterConditions),
          ],
        },
        sorting,
      },
      context: {
        debounceKey: GET_SRED_PAYROLL_EXPENSES_KEY,
      },
    },
    {
      getConnection: (data) => data.getSredPayrollExpenses,
      numElementsPerFetch: 40,
    },
  );
  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const sredPayrollExpenses = stickyData?.getSredPayrollExpenses?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      sredPayrollExpenses: sredPayrollExpenses,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    throw createError('MissingSredPayrollExpenses', 'No data returned from getSredPayrollExpenses query');
  }

  return {
    loading,
    sredPayrollExpenses,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
